import { auth } from '@/config/firebase'
import { useAuthStore } from '@/stores/auth'

export const COPILOT_API_URL = process.env.VUE_APP_COPILOT_API_URL || 'http://localhost:3001'

/**
 * AISLAMIENTO MULTI-TENANT (fuga #4): TODAS las llamadas a copilot-api deben mandar la
 * identidad real del usuario (idToken de Firebase) + el tenant activo (X-Tenant-Id). Sin eso,
 * el server caía al tenant de SU propio env y un usuario veía datos del tenant equivocado.
 *
 * Fail-closed: si no hay tenant activo (effectiveTenantId) NO se dispara la llamada; se lanza
 * un error para que el composable lo capture en `error` en vez de pegarle sin tenant.
 */

class CopilotTenantMissingError extends Error {
  constructor() {
    super('Sin tenant activo: no se puede consultar copilot-api')
    this.name = 'CopilotTenantMissingError'
  }
}

function requireTenantId(): string {
  const authStore = useAuthStore()
  const tenantId = authStore.effectiveTenantId
  if (!tenantId) throw new CopilotTenantMissingError()
  return tenantId
}

async function requireIdToken(): Promise<string> {
  const current = auth.currentUser
  if (!current) throw new Error('Sin sesión activa: no se puede consultar copilot-api')
  return current.getIdToken()
}

/**
 * Headers para fetch normal (no SSE): idToken real + tenant activo.
 * El server resuelve user→tenant por membresía y valida X-Tenant-Id (denyMismatch).
 */
export async function copilotHeaders(
  extra?: Record<string, string>,
): Promise<Record<string, string>> {
  const tenantId = requireTenantId()
  const idToken = await requireIdToken()
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${idToken}`,
    'X-Tenant-Id': tenantId,
    ...(extra || {}),
  }
}

/**
 * Params de auth para EventSource / streams SSE (no admiten headers custom):
 * `token` = idToken REAL de Firebase (el server lo valida como usuario, ruta `query-token`)
 * + `tenant_id` explícito. Ya NO se manda la API_KEY del bundle (TPL-93): el usuario real
 * viaja por query igual que el resto de superficies. El server EXIGE tenant_id en estas rutas
 * y NO cae al tenant del env.
 *
 * NOTA (TPL-93): el stream de sesiones (`/api/sessions/:name/stream`) ya NO usa esto — usa un
 * STREAM TICKET efímero (ver `requestStreamTicket`), para no exponer el idToken en la URL.
 * Esta función queda para los demás SSE (p.ej. `/api/graph/events`) que aún dependen de
 * `?token=` porque EventSource no manda headers.
 */
export async function copilotStreamAuthParams(): Promise<URLSearchParams> {
  const tenantId = requireTenantId()
  const idToken = await requireIdToken()
  return new URLSearchParams({ token: idToken, tenant_id: tenantId })
}

/**
 * TPL-93: pide un STREAM TICKET efímero (single-use, TTL corto) para abrir el SSE de una sesión
 * SIN exponer el idToken en la URL. Autenticado por HEADER (Bearer idToken + X-Tenant-Id, vía
 * `copilotHeaders`). El backend exige que la emisión sea header-only (401 si no).
 *
 * Se debe pedir un ticket FRESCO por cada (re)conexión del stream — el ticket es single-use +
 * TTL 45s. Fail-closed: propaga el error (sin tenant/sesión, 401/429/red) para que el caller
 * lo derive a su estado disconnected/reconnecting.
 *
 * @returns `{ ticket, expiresIn }` — `ticket` va como `?ticket=` (ya scopea user+tenant+sesión).
 */
export async function requestStreamTicket(
  sessionName: string,
): Promise<{ ticket: string; expiresIn: number }> {
  const headers = await copilotHeaders()
  const response = await fetch(
    `${COPILOT_API_URL}/api/sessions/${encodeURIComponent(sessionName)}/stream-ticket`,
    { method: 'POST', headers },
  )
  if (!response.ok) {
    let detail = ''
    try {
      detail = await response.text()
    } catch {
      detail = ''
    }
    throw new Error(`stream-ticket HTTP ${response.status}${detail ? `: ${detail}` : ''}`)
  }
  const data = (await response.json()) as { ticket: string; expiresIn: number }
  return data
}

/**
 * TPL-94 (V2 chat de agente — continuar chats): resumen de una sesión de agente V2 ACTIVA del
 * usuario (continuable). Lo devuelve `GET /api/agent-sessions`. Fail-closed por dueño + tenant.
 */
export interface AgentSessionSummary {
  streamId: string
  provider: string
  sessionName: string
  status: string
  projectId: string | null
  highWaterSeq: number
  createdAt: string
  updatedAt: string
}

/**
 * TPL-94 (V2 chat de agente — continuar chats): lista las agent-sessions ACTIVAS del usuario
 * (continuables) para reabrirlas en el tab de Chat V2. Header-only (Bearer idToken + X-Tenant-Id
 * vía `copilotHeaders`); el server filtra fail-closed por dueño + tenant. Propaga el error para
 * que el caller lo derive a su estado de "no se pudo listar".
 */
export async function listAgentSessions(): Promise<AgentSessionSummary[]> {
  const headers = await copilotHeaders()
  const response = await fetch(`${COPILOT_API_URL}/api/agent-sessions`, {
    method: 'GET',
    headers,
  })
  if (!response.ok) {
    let detail = ''
    try {
      detail = await response.text()
    } catch {
      detail = ''
    }
    throw new Error(`agent-sessions HTTP ${response.status}${detail ? `: ${detail}` : ''}`)
  }
  const data = (await response.json()) as { sessions?: AgentSessionSummary[] }
  return Array.isArray(data.sessions) ? data.sessions : []
}

/**
 * TPL-94 (F0 — selector de target): capacidades declaradas de un bridge. Bridges VIEJOS no las
 * mandan ({} vacío) → tratar caps ausentes/vacías como "capaz" (compat). Solo `v2 === false`
 * explícito significa que NO soporta V2.
 */
export interface AgentTargetCapabilities {
  v1?: boolean
  v2?: boolean
  providers?: string[]
}

/**
 * TPL-94 (F0 — selector de target): un bridge del usuario donde puede correr una sesión de agente
 * (p.ej. su Mac local o el VPS). Lo devuelve `GET /api/agents/targets` (fail-closed por dueño).
 */
export interface AgentTarget {
  targetId: string
  label: string
  capabilities: AgentTargetCapabilities
  online: boolean
  isDefault: boolean
  /** TPL-94 (navegador de carpetas): raíz absoluta a la que se acota el browse. Ausente = sin browse. */
  browseRoot?: string
  /** TPL-94: profundidad máxima navegable bajo browseRoot. */
  browseMaxDepth?: number
}

/** TPL-94 (navegador de carpetas): una carpeta hija devuelta por el listado del bridge. */
export interface TargetDirEntry {
  name: string
  path: string
}

/** TPL-94 (navegador de carpetas): resultado de `GET /api/agents/targets/:id/dirs`. */
export interface TargetDirsResult {
  root: string
  path: string
  dirs: TargetDirEntry[]
  atMaxDepth: boolean
}

/**
 * TPL-94 (F0 — selector de target): lista los bridges (targets) del usuario para elegir DÓNDE
 * corre la sesión de agente V2 (Mac vs VPS). Header-only (Bearer idToken + X-Tenant-Id vía
 * `copilotHeaders`), fail-closed por dueño. Parse DEFENSIVO: si el server no manda `targets`
 * (o falla el JSON) devolvemos []. El caller trata []/error como "0 targets" sin crashear.
 */
export async function fetchAgentTargets(): Promise<AgentTarget[]> {
  const headers = await copilotHeaders()
  const response = await fetch(`${COPILOT_API_URL}/api/agents/targets`, {
    method: 'GET',
    headers,
  })
  if (!response.ok) {
    let detail = ''
    try {
      detail = await response.text()
    } catch {
      detail = ''
    }
    throw new Error(`agents/targets HTTP ${response.status}${detail ? `: ${detail}` : ''}`)
  }
  let data: { targets?: unknown } = {}
  try {
    data = (await response.json()) as { targets?: unknown }
  } catch {
    return []
  }
  if (!Array.isArray(data.targets)) return []
  // Normalización defensiva: descartamos entradas sin targetId; caps ausentes → {}.
  return (data.targets as unknown[]).flatMap((raw): AgentTarget[] => {
    if (!raw || typeof raw !== 'object') return []
    const t = raw as Record<string, unknown>
    const targetId = typeof t.targetId === 'string' ? t.targetId : ''
    if (!targetId) return []
    const capsRaw =
      t.capabilities && typeof t.capabilities === 'object'
        ? (t.capabilities as Record<string, unknown>)
        : {}
    const capabilities: AgentTargetCapabilities = {
      v1: typeof capsRaw.v1 === 'boolean' ? capsRaw.v1 : undefined,
      v2: typeof capsRaw.v2 === 'boolean' ? capsRaw.v2 : undefined,
      providers: Array.isArray(capsRaw.providers)
        ? (capsRaw.providers as unknown[]).filter((p): p is string => typeof p === 'string')
        : undefined,
    }
    return [
      {
        targetId,
        label: typeof t.label === 'string' && t.label ? t.label : targetId,
        capabilities,
        online: t.online === true,
        isDefault: t.isDefault === true,
        browseRoot:
          typeof t.browseRoot === 'string' && t.browseRoot ? t.browseRoot : undefined,
        browseMaxDepth:
          typeof t.browseMaxDepth === 'number' ? t.browseMaxDepth : undefined,
      },
    ]
  })
}

/**
 * TPL-94 (navegador de carpetas): lista las carpetas hijas de `path` (o el nivel top de `browseRoot`
 * si no se pasa path) en el bridge del target. Header-only (`copilotHeaders`), acotado a `browseRoot`
 * por el backend. Parse defensivo. Propaga el error con el status HTTP para que el caller distinga
 * 409 (offline) / 502 (error del bridge) / 504 (timeout) / 429 (rate limit).
 */
export async function fetchTargetDirs(
  targetId: string,
  path?: string,
): Promise<TargetDirsResult> {
  const headers = await copilotHeaders()
  const params = new URLSearchParams()
  if (path) params.set('path', path)
  const qs = params.toString()
  const response = await fetch(
    `${COPILOT_API_URL}/api/agents/targets/${encodeURIComponent(targetId)}/dirs${qs ? `?${qs}` : ''}`,
    { method: 'GET', headers },
  )
  if (!response.ok) {
    let detail = ''
    try {
      detail = await response.text()
    } catch {
      detail = ''
    }
    throw new Error(`dirs HTTP ${response.status}${detail ? `: ${detail}` : ''}`)
  }
  const data = (await response.json()) as {
    root?: string
    path?: string
    dirs?: unknown
    atMaxDepth?: boolean
  }
  const dirs: TargetDirEntry[] = Array.isArray(data.dirs)
    ? (data.dirs as unknown[]).flatMap((raw): TargetDirEntry[] => {
        if (!raw || typeof raw !== 'object') return []
        const d = raw as Record<string, unknown>
        const p = typeof d.path === 'string' ? d.path : ''
        if (!p) return []
        return [{ name: typeof d.name === 'string' && d.name ? d.name : p, path: p }]
      })
    : []
  return {
    root: typeof data.root === 'string' ? data.root : '',
    path: typeof data.path === 'string' ? data.path : path || '',
    dirs,
    atMaxDepth: data.atMaxDepth === true,
  }
}

/**
 * TPL-94 (V2 chat de agente — terminar chat): cierra una sesión de agente V2 (marca 'ended', mata
 * la tmux del runtime; el SSE corta y la sesión sale del listado activo). Header-only (Bearer
 * idToken + X-Tenant-Id vía `copilotHeaders`). Propaga el error para que el caller lo derive a un
 * aviso claro (y NO cierre la vista si falló).
 */
export async function terminateAgentSession(
  streamId: string,
): Promise<{ streamId: string; status: string }> {
  const headers = await copilotHeaders()
  const response = await fetch(
    `${COPILOT_API_URL}/api/agent-sessions/${encodeURIComponent(streamId)}/terminate`,
    { method: 'POST', headers },
  )
  if (!response.ok) {
    let detail = ''
    try {
      detail = await response.text()
    } catch {
      detail = ''
    }
    throw new Error(`terminate HTTP ${response.status}${detail ? `: ${detail}` : ''}`)
  }
  const data = (await response.json()) as { streamId: string; status: string }
  return data
}

/**
 * TPL-94 (V2 chat de agente): stream-ticket efímero para el SSE ESTRUCTURADO de una agent-session.
 * Mismo patrón que `requestStreamTicket` de V1 pero contra `/api/agent-sessions/:streamId/stream-ticket`.
 * Header-only (Bearer idToken + X-Tenant-Id vía `copilotHeaders`), single-use + TTL corto: se pide
 * uno FRESCO por cada (re)conexión del stream (EventSource no manda headers → sin ticket, 401 loop
 * con token viejo tras expirar). Fail-closed: propaga el error para que el caller lo derive a su
 * estado disconnected/reconnecting.
 *
 * @returns `{ ticket, expiresIn }` — `ticket` va como `?ticket=` (ya scopea user+tenant+stream).
 */
export async function requestAgentStreamTicket(
  streamId: string,
): Promise<{ ticket: string; expiresIn: number }> {
  const headers = await copilotHeaders()
  const response = await fetch(
    `${COPILOT_API_URL}/api/agent-sessions/${encodeURIComponent(streamId)}/stream-ticket`,
    { method: 'POST', headers },
  )
  if (!response.ok) {
    let detail = ''
    try {
      detail = await response.text()
    } catch {
      detail = ''
    }
    throw new Error(`agent stream-ticket HTTP ${response.status}${detail ? `: ${detail}` : ''}`)
  }
  const data = (await response.json()) as { ticket: string; expiresIn: number }
  return data
}
